// Vite ビルド時 env と /config/runtime.json から取得するランタイム config を統合する。
// ローカル開発時は VITE_AUTH_MODE=mock 既定。デプロイ後はランタイム config が優先される。

import { z } from "zod";
import { isEmbedOrigin } from "./features/connect-feedback-window/embed-protocol";

const RuntimeConfigSchema = z.object({
  apiBaseUrl: z.string().min(1).default("/api"),
  authMode: z.enum(["mock", "required"]).default("mock"),
  authServiceOrigin: z.string().url().optional(),
  serviceOrigin: z.string().url().optional(),
  realtimeWebSocketUrl: z.string().min(1).optional(),
  invitationSignupUrl: z.string().url().optional(),
  // 参照専用の埋め込みモードでトークンを渡してくる親フレーム（Connect Note）の許可オリジン。
  // 未設定なら空配列（埋め込み無効相当）。postMessage の event.origin 検証に使う。
  embedParentOrigins: z.array(z.string().url()).default([]),
  // Connect Feedback の配信オリジン。設定され、かつ origin として妥当なときだけ
  // 右下 FAB から投稿フォームを埋め込む。未設定なら FAB を出さない。
  connectFeedbackOrigin: z.string().optional(),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

export interface WebConfig {
  readonly authMode: "mock" | "required";
  readonly apiBaseUrl: string;
  readonly authServiceOrigin?: string | undefined;
  readonly serviceOrigin?: string | undefined;
  readonly realtimeWebSocketUrl: string;
  readonly invitationSignupUrl?: string | undefined;
  readonly embedParentOrigins: readonly string[];
  readonly connectFeedbackOrigin?: string | undefined;
}

let cachedConfig: WebConfig | undefined;

/**
 * 起動時に呼び出して runtime config を読み込む。
 * 失敗した場合はビルド時 env で代替する（ローカル mock 動作の保険）。
 */
export async function initializeWebConfig(): Promise<WebConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }
  const buildTime = readBuildTimeEnv();
  let runtime: RuntimeConfig | undefined;
  try {
    const response = await fetch("/config/runtime.json", { cache: "no-cache" });
    if (response.ok) {
      runtime = RuntimeConfigSchema.parse(await response.json());
    }
  } catch {
    // ローカル開発などで runtime.json が存在しない場合はビルド時 env のみで動作する。
  }

  const authMode = runtime?.authMode ?? buildTime.authMode;
  cachedConfig = {
    authMode,
    apiBaseUrl: runtime?.apiBaseUrl ?? buildTime.apiBaseUrl,
    authServiceOrigin: runtime?.authServiceOrigin ?? buildTime.authServiceOrigin,
    serviceOrigin: runtime?.serviceOrigin ?? buildTime.serviceOrigin,
    realtimeWebSocketUrl: runtime?.realtimeWebSocketUrl ?? buildTime.realtimeWebSocketUrl,
    invitationSignupUrl: runtime?.invitationSignupUrl ?? buildTime.invitationSignupUrl,
    embedParentOrigins: runtime?.embedParentOrigins ?? buildTime.embedParentOrigins,
    // origin として妥当でない値は握りつぶして未設定扱いにする（誤設定で別サイトを
    // iframe 化しないため）。mock 認証のローカル開発だけ loopback の HTTP も許可する。
    connectFeedbackOrigin: resolveConnectFeedbackOrigin(
      runtime?.connectFeedbackOrigin ?? buildTime.connectFeedbackOrigin,
      authMode
    ),
  };
  return cachedConfig;
}

function resolveConnectFeedbackOrigin(
  value: string | undefined,
  authMode: "mock" | "required"
): string | undefined {
  return isEmbedOrigin(value, authMode === "mock") ? value : undefined;
}

export function getWebConfig(): WebConfig {
  if (!cachedConfig) {
    throw new Error("WebConfig が未初期化です。initializeWebConfig() を呼び出してください。");
  }
  return cachedConfig;
}

function readBuildTimeEnv(): WebConfig {
  const env = import.meta.env;
  const authMode = env.VITE_AUTH_MODE === "required" ? "required" : "mock";
  return {
    authMode,
    apiBaseUrl: env.VITE_API_BASE_URL ?? "/api",
    authServiceOrigin: env.VITE_MEIDEN_CONNECT_AUTH_SERVICE_ORIGIN,
    serviceOrigin: env.VITE_MEIDEN_CONNECT_SERVICE_ORIGIN,
    realtimeWebSocketUrl: env.VITE_REALTIME_WEBSOCKET_URL ?? "ws://127.0.0.1:8787/realtime",
    invitationSignupUrl: env.VITE_MEIDEN_CONNECT_INVITATION_SIGNUP_URL,
    // ローカル/ビルド時 env では埋め込み親を持たない（runtime.json 優先）。
    embedParentOrigins: [],
    connectFeedbackOrigin: env.VITE_CONNECT_FEEDBACK_ORIGIN,
  };
}
